import { useState, useEffect, type ImgHTMLAttributes } from "react";

export type AvatarSize = "sm" | "md" | "lg";

export interface AvatarProps
  extends Omit<ImgHTMLAttributes<HTMLImageElement>, "size"> {
  size?: AvatarSize;
  name?: string;
  src?: string;
}

const sizeStyles: Record<AvatarSize, string> = {
  sm: "w-8 h-8 text-xs",
  md: "w-10 h-10 text-sm",
  lg: "w-12 h-12 text-base",
};

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) {
    return parts[0].charAt(0).toUpperCase();
  }
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
}

function getColorFromName(name: string): string {
  const colors = [
    "bg-primary-500",
    "bg-secondary-500",
    "bg-success-500",
    "bg-warning-500",
    "bg-info-500",
  ];
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length];
}

export function Avatar({
  size = "md",
  name = "",
  src,
  alt,
  className = "",
  ...props
}: AvatarProps) {
  const [imgError, setImgError] = useState(false);

  // Reset error state when src changes (F4 fix)
  useEffect(() => {
    setImgError(false);
  }, [src]);

  const showFallback = !src || imgError;
  const initials = name ? getInitials(name) : "?";
  const bgColor = name ? getColorFromName(name) : "bg-neutral-500";

  if (showFallback) {
    return (
      <div
        className={`
          inline-flex items-center justify-center
          rounded-full font-medium text-white
          ${bgColor}
          ${sizeStyles[size]}
          ${className}
        `}
        aria-label={name || "Avatar"}
      >
        {initials}
      </div>
    );
  }

  return (
    <img
      src={src}
      alt={alt || name || "Avatar"}
      onError={() => setImgError(true)}
      className={`
        inline-block rounded-full object-cover
        ${sizeStyles[size]}
        ${className}
      `}
      {...props}
    />
  );
}
